import { IUser } from './user';

export type BedPosture = 'LYING_IN_BED' | 'SITTING_ON_BED' | 'OUT_OF_BED';

/** A prototype bed belongs to one sensor, never to facility_beds. */
export interface IBedState {
  logicalBedId: string;
  deviceId: string;
  deviceLabel: string;
  buildingId: string;
  unitId: string;
  roomId: string;
  state: BedPosture | null;
  stateSince: string | null;
  lastEventAt: string | null;
  lastReceivedAt: string | null;
  lastEventId: string | null;
}

export interface IBedStatesSnapshot {
  buildingId: string;
  asOf: string;
  items: IBedState[];
}

export interface IBedStateUpdate {
  type: 'bed_state_updated';
  buildingId: string;
  emittedAt: string;
  bed: IBedState;
}

export interface IBedStateHistory {
  logicalBedId: string;
  deviceId: string;
  deviceLabel: string;
  from: string;
  to: string;
  asOf: string;
  timezone: string;
  intervals: { state: BedPosture | null; start: string; end: string | null }[];
  summarySeconds: { lying: number; sitting: number; outOfBed: number; unknown: number };
}

export function canViewBedStates(user: IUser | null | undefined): boolean {
  return user?.role === 'superuser' && user.status !== 'INACTIVE';
}

export function isBedState(value: unknown): value is IBedState {
  if (!value || typeof value !== 'object') return false;
  const bed = value as IBedState;
  const dateOrNull = (date: unknown) => date === null || (typeof date === 'string' && Number.isFinite(Date.parse(date)));
  return ['deviceId', 'deviceLabel', 'buildingId', 'unitId', 'roomId'].every(key =>
    typeof (bed as unknown as Record<string, unknown>)[key] === 'string' && !!(bed as unknown as Record<string, unknown>)[key])
    && bed.logicalBedId === `sensor:${bed.deviceId}`
    && (bed.state === null || ['LYING_IN_BED', 'SITTING_ON_BED', 'OUT_OF_BED'].includes(bed.state))
    && [bed.stateSince, bed.lastEventAt, bed.lastReceivedAt].every(dateOrNull)
    && (bed.lastEventId === null || typeof bed.lastEventId === 'string')
    && (bed.state === null ? bed.stateSince === null : bed.stateSince !== null);
}

/** Same ordering as the BE: sensor timestamp, received timestamp, row ID. */
export function compareBedStates(left: IBedState, right: IBedState): number {
  for (const key of ['lastEventAt', 'lastReceivedAt'] as const) {
    const a = left[key] === null ? -Infinity : Date.parse(left[key]!);
    const b = right[key] === null ? -Infinity : Date.parse(right[key]!);
    if (a !== b) return a < b ? -1 : 1;
  }
  const a = left.lastEventId ?? '', b = right.lastEventId ?? '';
  return a === b ? 0 : a < b ? -1 : 1;
}
